import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useQuery } from '@tanstack/react-query';
import { Users as UsersIcon, CreditCard, UserPlus, Cpu, HardDrive, Wifi, Clock } from 'lucide-react';
import { api } from '../lib/api';
import { Card, CardContent } from '../components/ui';
function formatBytes(bytes) {
    if (bytes < 1024 * 1024)
        return `${(bytes / 1024).toFixed(1)} MB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
function formatUptime(seconds) {
    const d = Math.floor(seconds / 86400);
    const h = Math.floor((seconds % 86400) / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    if (d > 0)
        return `${d}d ${h}h`;
    if (h > 0)
        return `${h}h ${m}m`;
    return `${m}m`;
}
export default function Dashboard() {
    const { data } = useQuery({
        queryKey: ['stats'],
        queryFn: () => api('/api/admin/stats'),
    });
    const { data: host } = useQuery({
        queryKey: ['stats-host'],
        queryFn: () => api('/api/admin/stats/host'),
        refetchInterval: 30000,
    });
    const cards = [
        { label: 'Total Users', value: data?.users ?? 0, icon: UsersIcon, color: 'text-brand-500' },
        { label: 'Active Plans', value: data?.activePlans ?? 0, icon: CreditCard, color: 'text-emerald-500' },
        { label: 'New Users (7d)', value: data?.newUsers7d ?? 0, icon: UserPlus, color: 'text-violet-500' },
    ];
    const hostCards = host ? [
        { label: 'RAM (RSS)', value: formatBytes(host.data.memory.rss), icon: HardDrive, color: 'text-blue-500' },
        { label: 'Heap Used', value: formatBytes(host.data.memory.heapUsed), icon: HardDrive, color: 'text-cyan-500' },
        { label: 'Load Avg', value: host.data.loadAvg.map((l) => l.toFixed(2)).join(' / '), icon: Cpu, color: 'text-orange-500' },
        { label: 'Uptime', value: formatUptime(host.data.uptime), icon: Clock, color: 'text-green-500' },
        { label: 'WS Connections', value: String(host.data.wsConnections), icon: Wifi, color: 'text-indigo-500' },
    ] : [];
    return (_jsxs("div", { children: [_jsxs("div", { className: "mb-8", children: [_jsx("h1", { className: "text-2xl font-extrabold tracking-tight text-slate-900", children: "Dashboard" }), _jsx("p", { className: "mt-1 text-sm text-slate-500", children: "Overview of your platform activity." })] }), _jsx("div", { className: "grid grid-cols-1 md:grid-cols-3 gap-6 mb-8", children: cards.map((card) => (_jsx(Card, { children: _jsx(CardContent, { className: "p-6", children: _jsxs("div", { className: "flex items-center justify-between", children: [_jsxs("div", { children: [_jsx("p", { className: "text-sm font-medium text-slate-500", children: card.label }), _jsx("p", { className: "text-3xl font-extrabold mt-1 text-slate-900", children: card.value })] }), _jsx(card.icon, { className: `h-8 w-8 ${card.color}` })] }) }) }, card.label))) }), host && (_jsxs(_Fragment, { children: [_jsx("h2", { className: "text-lg font-bold text-slate-900 mb-4", children: "System Health" }), _jsx("div", { className: "grid grid-cols-1 md:grid-cols-5 gap-4", children: hostCards.map((card) => (_jsx(Card, { children: _jsx(CardContent, { className: "p-4", children: _jsxs("div", { className: "flex items-center justify-between", children: [_jsxs("div", { children: [_jsx("p", { className: "text-xs font-medium text-slate-500", children: card.label }), _jsx("p", { className: "text-xl font-bold mt-0.5 text-slate-900", children: card.value })] }), _jsx(card.icon, { className: `h-5 w-5 ${card.color}` })] }) }) }, card.label))) })] }))] }));
}
